/**
 * ListPageFilterBar – reusable filter row for V2 list pages.
 * Renders config-driven fields (text, date, select) + Search and Reset buttons.
 * Use inside Card for consistent list-page layout.
 */
import React from "react";
import { Button, InputField } from "../atoms";
import { theme } from "../theme/semanticTheme";
import styled from "styled-components";

const FILTER_ITEM_HEIGHT = 40;
const bp = theme.breakpoints || { sm: "640px" };

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const ResetIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  /* When fields render a title/label above inputs, align by input bottoms */
  align-items: flex-end;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.lg};
  margin-bottom: 0;
  input, select {
    min-height: ${FILTER_ITEM_HEIGHT}px;
    box-sizing: border-box;
  }
  & > button {
    min-height: ${FILTER_ITEM_HEIGHT}px;
  }
  @media (max-width: ${bp.sm}) {
    padding: ${theme.spacing.md};
  }
`;

const FieldWrap = styled.div`
  flex: 1;
  min-width: 120px;
  max-width: 180px;
  & > div {
    width: 100%;
  }
  &.dateTimeField {
    flex: 0 0 auto;
    width: fit-content;
    min-width: unset;
    max-width: none;

    & > div,
    input {
      width: fit-content;
    }
  }
  @media (max-width: ${bp.sm}) {
    max-width: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${theme.spacing.sm};
`;

const RangeErrorBanner = styled.div`
  flex-basis: 100%;
  margin-bottom: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.accentLight};
  border: 1px solid ${theme.colors.accent};
  color: ${theme.colors.accent};
  font-size: ${theme.typography.fontSize.sm};
`;

/**
 * @param {Object} props
 * @param {{ name: string, type: 'text'|'date'|'select', value: string, placeholder: string, options?: { value: string, label: string }[], maxLength?: number }[]} props.filters
 * @param {(name: string, value: string) => void} props.onFilterChange
 * @param {() => void} props.onSearch
 * @param {() => void} props.onReset
 * @param {boolean} [props.allowEmptySearch] – when true, Search is enabled even if all filters are empty (list-all behaviour)
 * @param {React.ReactNode} [props.children] – optional slot after fields, before buttons (e.g. autocomplete)
 */
export default function ListPageFilterBar({
  filters = [],
  onFilterChange,
  onSearch,
  onReset,
  allowEmptySearch = false,
  children,
}) {
  const getVal = (name) => (filters.find((f) => f.name === name)?.value ?? "");
  const hasAnyValue = filters.some((f) => {
    const v = f?.value;
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim() !== "";
    return Boolean(v);
  });
  const hasFromToNames = filters.some((f) => f.name === "fromDate") && filters.some((f) => f.name === "toDate");
  const hasDateFromToNames = filters.some((f) => f.name === "dateFrom") && filters.some((f) => f.name === "dateTo");
  const hasPair = hasFromToNames || hasDateFromToNames;
  const fromKey = hasFromToNames ? "fromDate" : "dateFrom";
  const toKey = hasFromToNames ? "toDate" : "dateTo";
  const fromVal = hasPair ? getVal(fromKey) : "";
  const toVal = hasPair ? getVal(toKey) : "";

  let dateRangeError = "";
  if (hasPair && (fromVal || toVal)) {
    if ((fromVal && !toVal) || (!fromVal && toVal)) {
      dateRangeError = "Please select both From and To dates.";
    } else if (fromVal && toVal) {
      const fromDate = new Date(fromVal);
      const toDate = new Date(toVal);
      if (fromDate > toDate) {
        dateRangeError = "From Date cannot be after To Date.";
      }
    }
  }

  const disableSearch =
    (!allowEmptySearch && !hasAnyValue) || (hasPair && !!dateRangeError);

  return (
    <Bar>
      {dateRangeError && hasPair && (
        <RangeErrorBanner>{dateRangeError}</RangeErrorBanner>
      )}
      {filters.map((f) => (
        <FieldWrap
          key={f.name}
          style={f.wrapStyle}
          className={f.type === "datetime-local" ? "dateTimeField" : ""}
        >
          {f.type === "select" ? (
            <InputField
              name={f.name}
              type="select"
              value={f.value}
              onChange={(v) => onFilterChange(f.name, v)}
              options={f.options || []}
              title={f.title ?? f.placeholder}
              placeholder={f.placeholder}
              placeholderOnly={f.placeholderOnly}
            />
          ) : (
            <InputField
              name={f.name}
              type={f.type || "text"}
              value={f.value}
              onChange={(v) => onFilterChange(f.name, v)}
              title={f.title ?? f.placeholder}
              placeholder={f.placeholder}
              maxLength={f.maxLength}
            />
          )}
        </FieldWrap>
      ))}
      {children}
      <ButtonGroup>
        <Button
          variant="primary"
          iconOnly
          leftIcon={<SearchIcon />}
          aria-label="Search"
          onClick={onSearch}
          disabled={disableSearch}
        />
        <Button variant="secondary" iconOnly leftIcon={<ResetIcon />} aria-label="Reset" onClick={onReset} />
      </ButtonGroup>
    </Bar>
  );
}
